// src/web/client/src/pages/Settings.jsx
//
// Settings (UX brief, Phase B): the store's details and email branding, plus —
// on the Platform plan — product consumption and the store type (from the old
// Replenish Settings). The email switches moved to Automations
// (components/automations/RetainEmailCards). Was StoreSettings.jsx. - English UI version
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Store, Paintbrush, Save, Eye, Upload, X, ImageIcon } from "lucide-react";
import PageTabs from "@/components/PageTabs";
import PageHeader from "@/components/PageHeader";
import { PlatformGate, PLATFORM_PAGES } from "@/pages/Platform";
import { useSubscriptionStatus } from "@/components/subscription/SubscriptionGuard";
import { API_BASE_URL } from "../constants";
import { authenticatedFetch, SubscriptionRequiredError } from "@/utils/authenticatedFetch";
import { getShopifySessionToken } from "@/utils/shopifyAuth";
export default function Settings() {
  const { toast } = useToast();
  const [shop, setShop] = React.useState(null);
  const subscription = useSubscriptionStatus();
  const hasBrain = subscription?.hasFeatureAccess?.('platform_access') === true;
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  // Email Preview state — html rendered server-side so we always match what
  // the customer would actually receive. Re-fetched (debounced) whenever the
  // merchant changes a brand value.
  const [previewHtml, setPreviewHtml] = React.useState("");
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  // Logo upload state.
  const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = React.useState(false);
  const logoInputRef = React.useRef(null);

  // Allowed types + size — kept in sync with backend ALLOWED_MIMES and
  // MAX_UPLOAD_BYTES in routes/branding.js. Client-side check just spares
  // the round-trip for obviously invalid files; the backend re-validates.
  const ALLOWED_LOGO_MIMES = new Set(["image/png", "image/jpeg", "image/svg+xml"]);
  const MAX_LOGO_BYTES = 1 * 1024 * 1024;

  // XHR-based multipart upload. Native fetch() in an embedded Shopify Admin
  // iframe goes through the fetch overrides installed in App.jsx PLUS
  // Shopify's own app-bridge fetch override — at least one of them hangs
  // forever on FormData bodies, so the promise never resolves and the
  // spinner sticks. Same reason utils/authenticatedFetch.js exists: it
  // notes "XHR-based fetch that bypasses Shopify's fetch override."
  //
  // We can't reuse authenticatedFetch directly because it hard-codes
  // Content-Type: application/json, which strips the multipart boundary
  // multer needs. So this helper inlines the XHR with two key differences:
  //   - No Content-Type header → browser sets multipart/form-data; boundary=...
  //   - 30s timeout (vs authenticatedFetch's 20s) for upload latency
  function xhrUpload(url, formData, token, shopDomain) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      if (shopDomain) xhr.setRequestHeader("X-Shop-Domain", shopDomain);
      // Deliberately NOT setting Content-Type — XHR sets it itself with the
      // multipart boundary when xhr.send(formData) is called.
      xhr.timeout = 30000;
      xhr.onload = () => {
        let data = {};
        try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON body */ }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.ontimeout = () => reject(new Error("Upload timed out after 30 seconds"));
      xhr.send(formData);
    });
  }

  const uploadLogoFile = React.useCallback(async (file) => {
    if (!file) return;
    if (!ALLOWED_LOGO_MIMES.has(file.type)) {
      toast({ title: "Unsupported file type", description: "Please choose a PNG, JPG, or SVG image.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast({ title: "File too large", description: "Logo must be 1 MB or smaller.", variant: "destructive" });
      return;
    }
    if (!shop?.shop_domain) {
      toast({ title: "Error", description: "Missing shop domain.", variant: "destructive" });
      return;
    }
    setIsUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      // shop_domain is also passed as the X-Shop-Domain header below — this
      // is belt-and-braces so the backend's resolveShopDomain (which checks
      // body, query, header, params in that order) finds it either way.
      fd.append("shop_domain", shop.shop_domain);

      let token;
      try { token = await getShopifySessionToken(); } catch { /* token optional */ }

      const res = await xhrUpload(
        `${API_BASE_URL}/branding/logo`,
        fd,
        token,
        shop.shop_domain
      );

      if (!res.ok) {
        toast({ title: "Upload failed", description: res.data.message || res.data.error || `HTTP ${res.status}`, variant: "destructive" });
        return;
      }
      setShop((prev) => ({
        ...prev,
        branding: { ...(prev?.branding || {}), logo_url: res.data.logo_url, logo_mime: res.data.logo_mime },
      }));
      toast({ title: "Logo uploaded", description: "Your logo is now part of every email." });
    } catch (err) {
      console.error("Logo upload error:", err);
      toast({ title: "Upload failed", description: err.message || "Network error", variant: "destructive" });
    } finally {
      // Always runs — the 30s xhr.timeout guarantees the promise resolves
      // or rejects in bounded time, so the spinner can never stick again.
      setIsUploadingLogo(false);
    }
  }, [shop?.shop_domain, toast]);

  const removeLogo = React.useCallback(async () => {
    if (!shop?.shop_domain) return;
    setIsUploadingLogo(true);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/branding/logo?shop_domain=${encodeURIComponent(shop.shop_domain)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: "Could not remove logo", description: data.message || data.error || `HTTP ${res.status}`, variant: "destructive" });
        return;
      }
      setShop((prev) => ({
        ...prev,
        branding: { ...(prev?.branding || {}), logo_url: "", logo_mime: "" },
      }));
      toast({ title: "Logo removed" });
    } catch (err) {
      if (!(err instanceof SubscriptionRequiredError)) {
        console.error("Logo delete error:", err);
        toast({ title: "Could not remove logo", description: err.message || "Network error", variant: "destructive" });
      }
    } finally {
      setIsUploadingLogo(false);
    }
  }, [shop?.shop_domain, toast]);

  const onLogoDrop = (e) => {
    e.preventDefault();
    setIsDraggingLogo(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadLogoFile(file);
  };

  React.useEffect(() => {
    loadShop();
  }, []);

  // Email Preview: refetch whenever brand inputs (or shop_name) change. The
  // preview endpoint is cheap (no DB, no AI) but we still debounce so the
  // <input type="color"> drag doesn't fire a request per pixel. 350ms feels
  // responsive without being chatty.
  React.useEffect(() => {
    if (!shop) return;
    const handle = setTimeout(async () => {
      setIsPreviewing(true);
      try {
        const res = await authenticatedFetch(`${API_BASE_URL}/email-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shop_name: shop.shop_name || "Your Store",
            shop_domain: shop.shop_domain || "your-store.myshopify.com",
            primary_color: shop.branding?.primary_color,
            logo_url: shop.branding?.logo_url,
            plan: shop.plan,
            type: "thank_you",
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setPreviewHtml(data.html || "");
        }
      } catch (err) {
        if (!(err instanceof SubscriptionRequiredError)) {
          console.error("Branding preview failed:", err);
        }
      } finally {
        setIsPreviewing(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [shop?.shop_name, shop?.shop_domain, shop?.plan, shop?.branding?.primary_color, shop?.branding?.logo_url]);

  const loadShop = async () => {
    try {
      const savedShopDomain = localStorage.getItem('shopDomain');
      let response;
      if (savedShopDomain) {
        response = await authenticatedFetch(`${API_BASE_URL}/shops?shop_domain=${savedShopDomain}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
      } else {
        response = await authenticatedFetch(`${API_BASE_URL}/shops`, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
      }
      const data = await response.json();
      //let shopData = Array.isArray(data) ? data[0] || null : data;
      let shopData = Array.isArray(data.shops) ? data.shops[0] || null : null;
      if (!shopData) {
        shopData = {
          shop_name: "",
          shop_domain: savedShopDomain || "",
          email: "",
          settings: {
            thank_you_enabled: true,
            review_request_enabled: true,
            vip_upsell_enabled: false,
            custom_offers_enabled: false,
            review_delay_days: 7,
            language: "en",
            timezone: "UTC"
          },
          branding: {
            primary_color: "#4F46E5",
            logo_url: ""
          },
          plan: "basic"
        };
      }
      setShop(shopData);
    } catch (error) {
      // 402 is handled by the central SubscriptionGuard overlay (PR #30) —
      // silently bail so we don't stack a red "Failed to load shop data"
      // toast on top of the friendly "Go to Billing" CTA. Real errors still
      // toast as before.
      if (error instanceof SubscriptionRequiredError) {
        return;
      }
      console.error("Failed to load shop:", error);
      toast({
        title: "Error",
        description: "Failed to load shop data.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const method = shop._id ? "PUT" : "POST";
      //const url = shop._id ? `${API_BASE_URL}/shops/${shop._id}` : `${API_BASE_URL}/shops`;
      const url = shop.shop_domain ? `${API_BASE_URL}/shops/${shop.shop_domain}` : `${API_BASE_URL}/shops`;
      const response = await authenticatedFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(shop)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const result = await response.json();

      if (shop.shop_domain) localStorage.setItem('shopDomain', shop.shop_domain);

      toast({
        title: "Success",
        description: "Shop settings saved successfully."
      });

      if (result._id) setShop(prev => ({ ...prev, _id: result._id }));
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Saving failed: " + error.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  /*const handleShopifyConnect = () => {
    const currentShop = shop?.shop_domain || '';
    if (!currentShop) {
      toast({
        title: "Error",
        description: "Please enter the shop domain first.",
        variant: "destructive"
      });
      return;
    }
    const shopDomain = currentShop.includes('.myshopify.com') ? currentShop : `${currentShop}.myshopify.com`;
    window.location.href = `https://api.ecomoria.com/auth?shop=${shopDomain}`;
  };*/

  // תיקון לפונקציה handleShopifyConnect ב-StoreSettings.jsx

const handleShopifyConnect = () => {
  // קח shop_domain מה-state או מ-localStorage
  const currentShop = shop?.shop_domain || localStorage.getItem('shop_domain') || '';
  
  console.log('Attempting to connect shop:', currentShop);
  
  if (!currentShop || currentShop === 'undefined' || currentShop === 'null') {
    toast({
      title: "Error",
      description: "Please enter your shop domain first (e.g., your-store.myshopify.com)",
      variant: "destructive"
    });
    return;
  }
  
  // אם זה demo shop, אל תיתן להתחבר
  if (currentShop === 'demo-shop.myshopify.com') {
    toast({
      title: "Demo Store",
      description: "Cannot connect demo store to Shopify. Please enter your real store domain.",
      variant: "destructive"
    });
    return;
  }
  
  // נרמל את הדומיין
  const shopDomain = currentShop.includes('.myshopify.com') 
    ? currentShop 
    : `${currentShop}.myshopify.com`;
  
  // שמור ב-localStorage לפני הפנייה
  localStorage.setItem('shop_domain', shopDomain);
  localStorage.setItem('shopDomain', shopDomain);
  
  console.log('Redirecting to OAuth with shop:', shopDomain);
  
  // הפנה ל-Shopify OAuth
  window.location.href = `https://api.ecomoria.com/auth?shop=${shopDomain}`;
};

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p>Loading shop settings...</p>
        </div>
      </div>
    );
  }

  // Defensive: if the load failed (e.g. 402 / network) we'd otherwise crash
  // a few lines down at `shop.shop_name`. The SubscriptionGuard overlay covers
  // the 402 case visually, but we still must not render the form against a
  // null shop — that throws and bubbles up to the ErrorBoundary, which is
  // worse UX than showing nothing here while the overlay is up.
  if (!shop) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-gray-500 text-sm">Shop settings unavailable.</p>
      </div>
    );
  }

  const storeTab = (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700">
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
        {/* Store Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Store Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="shop_name">Store Name</Label>
                {/* Read-only: the name has ONE source — Shopify (Settings → Store
                    details), synced at install and on every rename. It signs
                    every email and heads the menu; editing it here created a
                    second value that disagreed with the first. */}
                <Input
                  id="shop_name"
                  value={shop.shop_name || ''}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-500 cursor-not-allowed"
                  placeholder="My Awesome Store"
                />
                <p className="text-xs text-gray-500">Set in Shopify (Settings → Store details) — changes there appear here.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="shop_domain">Store Domain</Label>
                {/* Read-only: the domain is the shop's identity (set by Shopify)
                    and drives the save URL. Editing it here never persisted (not
                    in the save whitelist) AND re-targeted the save to a wrong /
                    missing shop → a failed save. Locking the field removes that
                    footgun. */}
                <Input
                  id="shop_domain"
                  value={shop.shop_domain || ''}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-500 cursor-not-allowed"
                  placeholder="mystore.myshopify.com"
                />
                <p className="text-xs text-gray-500">Set by Shopify — this can't be changed here.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Contact email</Label>
              <Input id="email" type="email" value={shop.shop_email || ''} onChange={(e) => setShop({ ...shop, shop_email: e.target.value })} placeholder="you@mystore.com" />
              {/* Honest scoping: shop_email is the MERCHANT's own address (account
                  notices — messageScheduler sends here), never the sender/reply-to
                  the customer sees (that's the store name + a fixed system from
                  address). Say so, so nobody expects this to change the email. */}
              <p className="text-xs text-gray-500">
                We'll email you here about your account. Customers never see this — your emails are sent from your store name.
              </p>
             </div>
            <div className="pt-4">
              {/* HIDDEN for Shopify Review
              <Button onClick={handleShopifyConnect} variant="outline" className="w-full">
                <Store className="w-4 h-4 mr-2" />
                Connect to Shopify
              </Button>
              */}
            </div>
          </CardContent>
        </Card>

        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Paintbrush className="w-5 h-5" />
              Branding
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <Input
                id="primary_color"
                type="color"
                className="h-10 w-20 p-1 cursor-pointer"
                value={shop.branding?.primary_color || "#6366f1"}
                onChange={(e) =>
                  setShop({ ...shop, branding: { ...shop.branding, primary_color: e.target.value } })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Logo</Label>
              {shop.branding?.logo_url ? (
                /* Current-logo view: thumbnail + Replace / Remove */
                <div className="flex items-center gap-4 p-4 border rounded-lg bg-gray-50">
                  <div className="w-20 h-20 rounded-md bg-white border flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img
                      src={shop.branding.logo_url}
                      alt="Current logo"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {shop.branding.logo_mime || "Image"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">Shown in the header of every email.</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Replace
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={removeLogo}
                      disabled={isUploadingLogo}
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                /* Empty-state view: drag-and-drop area */
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingLogo(true);
                  }}
                  onDragLeave={() => setIsDraggingLogo(false)}
                  onDrop={onLogoDrop}
                  onClick={() => !isUploadingLogo && logoInputRef.current?.click()}
                  className={`flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                    isDraggingLogo
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
                  } ${isUploadingLogo ? "opacity-50 cursor-wait" : ""}`}
                >
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                  <p className="text-sm font-medium text-gray-700">
                    {isUploadingLogo ? "Uploading…" : "Drop your logo here, or click to choose"}
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, or SVG — up to 1 MB</p>
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogoFile(file);
                  e.target.value = ""; /* allow re-selecting the same file */
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Email Preview
              {isPreviewing && (
                <span className="text-xs font-normal text-gray-400 ml-2">Updating…</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-3">
              Live preview of how a thank-you email looks with your branding. Updates as you edit.
            </p>
            {previewHtml ? (
              <div className="border rounded-lg overflow-hidden bg-white">
                <iframe
                  title="Email branding preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full"
                  style={{ height: 600, border: "none", background: "#f3f4f6" }}
                />
              </div>
            ) : (
              <div className="border rounded-lg p-12 text-center text-gray-400 text-sm">
                Loading preview…
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <PageHeader title="Settings" subtitle="Your store's details, email branding and — with the Platform plan — how your products are used" />
        <PageTabs tabs={[
          { id: 'store', label: 'Store', render: () => storeTab },
          // Product consumption + store type: the old Replenish Settings, minus
          // the reminder timing (that is an automation — see Automations →
          // Restock reminders). Platform plan only; other plans see the locked
          // state the gate renders.
          ...(hasBrain ? [{ id: 'products', label: 'Products', render: () => <PlatformGate><PLATFORM_PAGES.replenishProducts /></PlatformGate> }] : []),
        ]} />
      </div>
    </div>
  );
}
